/**
 * Everything the Flights page does that is not rendering. (2026-09-12)
 *
 * A watch's `fields` are the watch-list contract's own (common_utils.watchlist
 * on the server), so this module names contract fields directly. The page
 * offers five forms over the contract's three kinds. A flights row is "a
 * flight I booked" or "a flight I'm eyeing" by its `booked` flag. A queries
 * row is a flexible trip when `min_nights` is set, which is site-checker's
 * range mode, and a round trip on set dates when it is not, which is its
 * fixed-date watcher.
 */

import { plural } from "../../../shared/captureFormat";
import { authedFetch } from "./api";
import type { PriceSeries, WatchOptions } from "./flightPrices";
import { orThrow } from "./http";

export type Kind = "flight" | "query" | "hotel";
export type Source = "you" | "sheet" | "tripit";
export type Value = string | number | boolean | string[] | null;
export type Fields = Record<string, Value>;

export interface Trip {
  id: number;
  name: string;
  source: Source;
  archived: boolean;
  /** Whose trip this is, as typed. Null or absent means your own. */
  traveler?: string | null;
  /** A merged trip remains as an alias so its TripIt id follows the survivor. */
  merged_into_id?: number | null;
}

export interface Watch {
  watch_id: string;
  kind: Kind;
  trip_id: number;
  source: Source;
  archived: boolean;
  /** Explicit monitoring state from wallet; absent only in old cached data. */
  watching?: boolean;
  /** The day TripIt stopped listing it. Flagged for you, never deleted. */
  missing_since: string | null;
  fields: Fields;
}

export interface Match {
  id: number;
  watch_id: string | null;
  rule: string;
  /** The TripIt leg, as contract flight fields. */
  proposed: Fields;
  created_at: string;
}

export interface FlightsDto {
  trips: Trip[];
  watches: Watch[];
  matches: Match[];
  /** site-checker's readings by watch_id (see flightPrices.ts). */
  prices?: Record<string, PriceSeries[]>;
  /** Each search's latest round trips, by watch_id and axis. */
  options?: Record<string, WatchOptions>;
  /** Exact price observations dismissed from the Travel page. */
  price_reviews?: Record<string, { cash?: string; award?: string }>;
}

/** What a watch PATCH may carry besides contract fields. */
export interface WatchMeta {
  trip_id?: number;
  archived?: boolean;
  watching?: boolean;
  missing_since?: null;
}

// -- API ----------------------------------------------------------------------

async function send(path: string, method: string, fallback: string, body?: unknown) {
  const res = await authedFetch(path, {
    method,
    ...(body === undefined
      ? {}
      : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  });
  return orThrow(res, fallback);
}

export async function fetchFlights(): Promise<FlightsDto> {
  const res = await authedFetch("/api/flights");
  return (await orThrow(res, "Could not load flights")) as FlightsDto;
}

export type FlightJob = "tripit" | "full" | "tripwire";

export interface StartedJob {
  job: FlightJob;
  label: string;
  status: "started";
}

export async function runFlightJob(job: FlightJob): Promise<StartedJob> {
  return (await send(
    `/api/flights/jobs/${job}/run`,
    "POST",
    "Could not start that job",
  )) as StartedJob;
}

export async function createTrip(name: string, traveler?: string): Promise<Trip> {
  const body = (await send("/api/flights/trips", "POST", "Could not add the trip", {
    name,
    ...(traveler === undefined ? {} : { traveler }),
  })) as {
    trip: Trip;
  };
  return body.trip;
}

export async function updateTrip(
  tripId: number,
  changes: { name?: string; archived?: boolean; traveler?: string },
): Promise<void> {
  await send(`/api/flights/trips/${tripId}`, "PATCH", "Could not update the trip", changes);
}

/** Archives the trip and every watch in it. Nothing is deleted. */
export async function archiveTrip(tripId: number): Promise<void> {
  await send(`/api/flights/trips/${tripId}`, "DELETE", "Could not archive the trip");
}

export async function mergeTrip(tripId: number, targetTripId: number): Promise<void> {
  await send(`/api/flights/trips/${tripId}/merge`, "POST", "Could not merge the trips", {
    target_trip_id: targetTripId,
  });
}

export async function createWatch(kind: Kind, tripId: number, fields: Fields): Promise<Watch> {
  const body = (await send("/api/flights/watches", "POST", "Could not start tracking it", {
    kind,
    trip_id: tripId,
    ...fields,
  })) as { watch: Watch };
  return body.watch;
}

export async function updateWatch(watchId: string, changes: Fields & WatchMeta): Promise<void> {
  await send(
    `/api/flights/watches/${encodeURIComponent(watchId)}`,
    "PATCH",
    "Could not save the change",
    changes,
  );
}

export async function archiveWatch(watchId: string): Promise<void> {
  await send(`/api/flights/watches/${encodeURIComponent(watchId)}`, "DELETE", "Could not archive it");
}

export async function reviewPrice(
  watchId: string,
  axis: "cash" | "award",
  observedAt: string,
): Promise<void> {
  await send(
    `/api/flights/watches/${encodeURIComponent(watchId)}/prices/${axis}/review`,
    "POST",
    "Could not mark the price reviewed",
    { observed_at: observedAt },
  );
}

export async function resolveMatch(matchId: number, action: "merge" | "separate"): Promise<void> {
  await send(`/api/flights/matches/${matchId}/resolve`, "POST", "Could not answer TripIt", {
    action,
  });
}

/** Close a TripIt proposal without making or changing a watch. */
export async function dismissMatch(matchId: number): Promise<void> {
  await send(`/api/flights/matches/${matchId}/dismiss`, "POST", "Could not dismiss that");
}

// -- forms --------------------------------------------------------------------

export type Form = "booked" | "eyeing" | "roundtrip" | "flex" | "hotel";

export const FORMS: Record<Form, { kind: Kind; title: string; blurb: string }> = {
  booked: {
    kind: "flight",
    title: "A flight I booked",
    blurb: "Watch for a drop to rebook or claim credit. Most arrive from TripIt on their own.",
  },
  eyeing: {
    kind: "flight",
    title: "A flight I'm eyeing",
    blurb: "One leg on a set day, or any day up to a second date, with an email when it drops.",
  },
  roundtrip: {
    kind: "query",
    title: "A round trip on set dates",
    blurb: "Out and back on fixed days, priced in cash and points.",
  },
  flex: {
    kind: "query",
    title: "A flexible trip",
    blurb: "A window of dates and trip lengths. Finds the cheapest combination.",
  },
  hotel: {
    kind: "hotel",
    title: "A hotel",
    blurb: "A stay on set dates, booked or not, with an email when the rate drops.",
  },
};

export function formOf(w: Pick<Watch, "kind" | "fields">): Form {
  if (w.kind === "hotel") return "hotel";
  if (w.kind === "query") return w.fields.min_nights != null ? "flex" : "roundtrip";
  return w.fields.booked ? "booked" : "eyeing";
}

export type Input =
  | "text"
  | "airport"
  | "airports"
  | "list"
  | "date"
  | "time"
  | "number"
  | "amount"
  | "check"
  | "cabin"
  | "program"
  | "programs"
  | "booked-via";

export interface FieldSpec {
  name: string;
  label: string;
  input: Input;
  required?: boolean;
  hint?: string;
  /** For `amount`: the checkbox that sends "ignored" (the sheet's "-"). */
  skip?: string;
  /** Behind "More filters". */
  more?: boolean;
}

export const CABINS = ["economy", "premium_economy", "business", "first"];

/** seats.aero source codes accepted by site-checker's partner API client. */
export const AWARD_PROGRAMS = [
  ["aeromexico", "Aeromexico Rewards"],
  ["aeroplan", "Air Canada Aeroplan"],
  ["alaska", "Alaska Atmos Rewards"],
  ["american", "American AAdvantage"],
  ["azul", "Azul Fidelidade"],
  ["delta", "Delta SkyMiles"],
  ["emirates", "Emirates Skywards"],
  ["ethiopian", "Ethiopian ShebaMiles"],
  ["etihad", "Etihad Guest"],
  ["eurobonus", "SAS EuroBonus"],
  ["finnair", "Finnair Plus"],
  ["flyingblue", "Air France/KLM Flying Blue"],
  ["frontier", "Frontier Miles"],
  ["jetblue", "JetBlue TrueBlue"],
  ["lufthansa", "Lufthansa Miles & More"],
  ["qantas", "Qantas Frequent Flyer"],
  ["qatar", "Qatar Privilege Club"],
  ["saudia", "Saudia AlFursan"],
  ["singapore", "Singapore KrisFlyer"],
  ["smiles", "GOL Smiles"],
  ["spirit", "Spirit Free Spirit"],
  ["turkish", "Turkish Miles & Smiles"],
  ["united", "United MileagePlus"],
  ["velocity", "Virgin Australia Velocity"],
  ["virginatlantic", "Virgin Atlantic Flying Club"],
] as const;

/**
 * Programs a single flight watch can name: the seats.aero codes plus Southwest,
 * which site-checker prices on southwest.com. Not offered on trip searches,
 * whose programs go to seats.aero only.
 */
export const FLIGHT_AWARD_PROGRAMS = [...AWARD_PROGRAMS, ["southwest", "Southwest Rapid Rewards"]]
  .sort((a, b) => a[0].localeCompare(b[0])) as ReadonlyArray<readonly [string, string]>;

/** Canonical values understood by site-checker's hotel alert modes. */
export const BOOKING_CHANNELS = [
  ["", "Direct"],
  ["amex", "Amex Travel"],
  ["chase", "Chase Travel"],
  ["bilt", "Bilt"],
] as const;

const ORIGIN: FieldSpec = { name: "origin", label: "From", input: "airport", required: true };
const DESTINATION: FieldSpec = { name: "destination", label: "To", input: "airport", required: true };
const CABIN: FieldSpec = { name: "cabin", label: "Cabin", input: "cabin" };
const NONSTOP: FieldSpec = { name: "nonstop", label: "Nonstop only", input: "check" };
const PROGRAM: FieldSpec = {
  name: "program",
  label: "Award program",
  input: "program",
  hint: "Leave blank to search every seats.aero program. Southwest is priced only when chosen here.",
};
const QUERY_NAME: FieldSpec = {
  name: "name",
  label: "Name",
  input: "text",
  required: true,
  hint: "Unique. site-checker names its results after it.",
};
const QUERY_ALERTS: FieldSpec[] = [
  { name: "cash_threshold", label: "Email me if cash is under ($)", input: "number" },
  { name: "points_threshold", label: "Email me if points are under", input: "number" },
  {
    name: "programs",
    label: "Award programs",
    input: "programs",
    hint: "Choose specific programs, or leave every box clear to search them all.",
  },
  { name: "active", label: "Active", input: "check" },
];
const ROUTE_FILTERS: FieldSpec[] = [
  { name: "connect_into", label: "Connect via", input: "airports", more: true },
  { name: "earliest_hour", label: "Depart after (hour)", input: "number", more: true },
  { name: "latest_arrival_hour", label: "Arrive before (hour)", input: "number", more: true },
  { name: "return_earliest_hour", label: "Return: depart after (hour)", input: "number", more: true },
  {
    name: "outbound_latest_arrival_hour",
    label: "Outbound: arrive before (hour)",
    input: "number",
    more: true,
  },
  { name: "min_layover", label: "Layover, at least (minutes)", input: "number", more: true },
  { name: "max_layover", label: "Layover, at most (minutes)", input: "number", more: true },
];

export const FIELDS: Record<Form, FieldSpec[]> = {
  booked: [
    ORIGIN,
    DESTINATION,
    { name: "date", label: "Date", input: "date", required: true },
    { name: "flight", label: "Flight", input: "text", hint: "AS 1170. Connections: WN 4765, WN 423." },
    { name: "departs", label: "Departs", input: "time" },
    CABIN,
    {
      name: "fare_tier",
      label: "Fare tier",
      input: "text",
      hint: "The airline's name for the fare you bought, e.g. Nicer. Blank prices the cheapest non-basic fare.",
    },
    {
      name: "price_tracked",
      label: "Paid ($)",
      input: "amount",
      hint: "Drops are measured from this.",
      skip: "Don't price it in cash",
    },
    PROGRAM,
    { name: "points_tracked", label: "Paid in points", input: "amount", skip: "Don't price it in points" },
    NONSTOP,
  ],
  eyeing: [
    ORIGIN,
    DESTINATION,
    { name: "date", label: "Leave", input: "date", required: true },
    { name: "date_end", label: "Or any day up to", input: "date" },
    { name: "flight", label: "Flight (optional)", input: "text", hint: "Blank means any flight." },
    CABIN,
    { name: "price_tracked", label: "Email me under ($)", input: "amount", skip: "Don't price it in cash" },
    PROGRAM,
    {
      name: "points_tracked",
      label: "Email me under (points)",
      input: "amount",
      skip: "Don't price it in points",
    },
    NONSTOP,
  ],
  roundtrip: [
    QUERY_NAME,
    ORIGIN,
    {
      name: "destinations",
      label: "To",
      input: "airports",
      required: true,
      hint: "Comma-separate to watch several.",
    },
    { name: "outbound_date", label: "Leave", input: "date", required: true },
    { name: "return_date", label: "Return", input: "date", required: true },
    CABIN,
    ...QUERY_ALERTS,
    ...ROUTE_FILTERS,
    {
      name: "long_haul",
      label: "Long haul (flights may land the next day; price the round-trip fare)",
      input: "check",
      more: true,
    },
    {
      name: "outbound_flight",
      label: "Booked outbound flights",
      input: "text",
      more: true,
      hint: "AS 815, AS 831. With the return flights set, prices exactly this round trip.",
    },
    { name: "outbound_departs", label: "Booked outbound departs", input: "time", more: true },
    { name: "return_flight", label: "Booked return flights", input: "text", more: true },
    { name: "return_departs", label: "Booked return departs", input: "time", more: true },
  ],
  flex: [
    QUERY_NAME,
    ORIGIN,
    {
      name: "destinations",
      label: "To",
      input: "airports",
      required: true,
      hint: "A flexible trip searches the first airport only.",
    },
    { name: "outbound_date", label: "Leave on or after", input: "date", required: true },
    { name: "return_date", label: "Back by", input: "date", required: true },
    { name: "min_nights", label: "Nights, at least", input: "number", required: true },
    { name: "max_nights", label: "Nights, at most", input: "number" },
    CABIN,
    ...QUERY_ALERTS,
    ...ROUTE_FILTERS,
    { name: "out_miles_cap", label: "Miles cap, outbound", input: "number", more: true },
    { name: "ret_miles_cap", label: "Miles cap, return", input: "number", more: true },
    {
      name: "exclude_ranges",
      label: "Skip these dates",
      input: "text",
      more: true,
      hint: "2026-07-04:2026-07-06; 2026-07-10",
    },
  ],
  hotel: [
    { name: "name", label: "Hotel", input: "text", required: true },
    { name: "city_code", label: "City (airport code)", input: "airport" },
    { name: "check_in", label: "Check in", input: "date", required: true },
    { name: "check_out", label: "Check out", input: "date", required: true },
    {
      name: "room",
      label: "Room type",
      input: "text",
      hint: "e.g. 2 queen suite. Every word must appear in the rate's room name. Blank prices the cheapest room.",
    },
    { name: "adults", label: "Adults", input: "number" },
    {
      name: "price_tracked",
      label: "Paid, or email me under ($)",
      input: "amount",
      skip: "Don't price it",
    },
    { name: "booked", label: "Booked", input: "check" },
    { name: "booked_via", label: "Booked via", input: "booked-via" },
    { name: "free_cancellation", label: "Free cancellation", input: "check" },
    { name: "refundable_until", label: "Refundable until", input: "date" },
  ],
};

/** A form's raw state: strings for inputs, booleans for checkboxes. */
export type FormValues = Record<string, string | boolean>;

const skipKey = (name: string) => `${name}:skip`;

/** The form's starting values: a stored watch's, or a new one's defaults. */
export function formValues(form: Form, watch?: Watch | null): FormValues {
  const out: FormValues = {};
  for (const spec of FIELDS[form]) {
    const v = watch ? watch.fields[spec.name] : undefined;
    if (spec.input === "check") {
      out[spec.name] = watch ? Boolean(v) : spec.name === "active";
    } else if (spec.input === "amount") {
      out[skipKey(spec.name)] = v === "ignored";
      out[spec.name] = v == null || v === "ignored" ? "" : String(v);
    } else if (Array.isArray(v)) {
      out[spec.name] = v.join(", ");
    } else if (spec.input === "cabin" && !watch) {
      out[spec.name] = "economy";
    } else {
      out[spec.name] = v == null ? "" : String(v);
    }
  }
  return out;
}

function asText(v: string | boolean | undefined): string {
  return typeof v === "string" ? v.trim() : "";
}

/**
 * The contract fields a form's values stand for. Blank is null. Numbers and
 * lists are parsed here; the server checks everything else and says what it
 * refused.
 */
export function fieldsFromForm(form: Form, values: FormValues): Fields {
  const out: Fields = {};
  for (const spec of FIELDS[form]) {
    const raw = values[spec.name];
    if (spec.input === "check") {
      out[spec.name] = Boolean(raw);
    } else if (spec.input === "amount" && values[skipKey(spec.name)]) {
      out[spec.name] = "ignored";
    } else if (spec.input === "amount" || spec.input === "number") {
      const text = asText(raw).replace(/[$,]/g, "");
      out[spec.name] = text === "" ? null : Number(text);
    } else if (spec.input === "airports" || spec.input === "list" || spec.input === "programs") {
      const items = asText(raw)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      out[spec.name] = items.length ? items : null;
    } else {
      const text = asText(raw);
      out[spec.name] = text === "" ? null : text;
    }
  }
  if (form === "booked") out.booked = true;
  if (form === "eyeing") out.booked = false;
  return out;
}

/** The first required field left blank, as its label, or null. */
export function missingRequired(form: Form, fields: Fields): string | null {
  const spec = FIELDS[form].find((s) => s.required && fields[s.name] == null);
  return spec ? spec.label : null;
}

/** Only what changed, so an edit never rewrites a field nobody touched. */
export function changedFields(before: Fields, after: Fields): Fields {
  const out: Fields = {};
  for (const [k, v] of Object.entries(after)) {
    if (JSON.stringify(before[k] ?? null) !== JSON.stringify(v)) out[k] = v;
  }
  return out;
}

/** For a new watch: drop what is blank rather than send nulls. */
export function withoutBlanks(fields: Fields): Fields {
  return Object.fromEntries(Object.entries(fields).filter(([, v]) => v != null));
}

// -- reading a watch ------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A date as YYYY-MM-DD, or null. Imported rows keep the sheet's own text, and
 * the sheet has some M/D/YYYY; anything else is not a date we can sort.
 */
export function toIso(value: Value | undefined): string | null {
  if (typeof value !== "string") return null;
  const s = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
}

/** "Jun 17". Parsed by hand: `new Date("2026-06-17")` is UTC midnight, the day before in Pacific. */
export function shortDate(value: Value | undefined): string {
  const iso = toIso(value);
  if (!iso) return typeof value === "string" ? value : "";
  const [, m, d] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

function nights(a: Value | undefined, b: Value | undefined): number | null {
  const x = toIso(a);
  const y = toIso(b);
  if (!x || !y) return null;
  const day = (iso: string) => {
    const [yy, mm, dd] = iso.split("-").map(Number);
    return Date.UTC(yy, mm - 1, dd) / 864e5;
  };
  return Math.round(day(y) - day(x));
}

export function watchStart(w: Watch): string | null {
  const f = w.fields;
  return toIso(w.kind === "flight" ? f.date : w.kind === "query" ? f.outbound_date : f.check_in);
}

export function watchEnd(w: Watch): string | null {
  const f = w.fields;
  if (w.kind === "flight") return toIso(f.date_end) ?? toIso(f.date);
  return toIso(w.kind === "query" ? f.return_date : f.check_out);
}

export function routeLabel(w: Watch): string {
  const f = w.fields;
  if (w.kind === "hotel") return f.city_code ? `${f.name} · ${f.city_code}` : String(f.name ?? "");
  const to = w.kind === "query" ? ((f.destinations as string[] | null) ?? []).join(", ") : f.destination;
  const arrow = w.kind === "query" && formOf(w) === "roundtrip" ? "⇄" : "→";
  return `${f.origin ?? "?"} ${arrow} ${to || "?"}`;
}

export function whenLabel(w: Watch): string {
  const f = w.fields;
  const form = formOf(w);
  if (form === "booked" || form === "eyeing") {
    const day = f.date_end ? `${shortDate(f.date)} – ${shortDate(f.date_end)}` : shortDate(f.date);
    return [day, f.flight, f.departs].filter(Boolean).join(" · ");
  }
  if (form === "flex") {
    const lo = f.min_nights as number;
    const hi = f.max_nights as number | null;
    const span = hi != null && hi !== lo ? `${lo}–${hi} nights` : `${plural(lo, "night")}${hi == null ? " or more" : ""}`;
    return `leave ${shortDate(f.outbound_date)} – ${shortDate(f.return_date)} · ${span}`;
  }
  const [a, b] = form === "hotel" ? [f.check_in, f.check_out] : [f.outbound_date, f.return_date];
  const n = nights(a, b);
  return `${shortDate(a)} → ${shortDate(b)}${n != null ? ` · ${plural(n, "night")}` : ""}`;
}

/** The chip that says what kind of watch this is. */
/**
 * A booked hotel whose free-cancellation date has passed.
 *
 * site-checker stops pricing these: past the deadline a cheaper rate is
 * something you cannot act on, so the search is not worth paying for. That
 * leaves `Last Checked` frozen, which on this page is the documented tell for
 * a broken row -- hence the chip, so a finished row does not read as a
 * failing one. Said as a plain fact about the booking, not as "no longer
 * priced": the Amex channel keeps its Lowest Rate Guarantee to check-in and
 * is still checked, and which channels those are belongs in one repo, not two.
 */
export function lockedIn(w: Watch, today: string): boolean {
  if (w.kind !== "hotel" || !w.fields.booked) return false;
  const until = w.fields.refundable_until;
  return typeof until === "string" && until.length > 0 && until < today;
}

export function badge(w: Watch): string {
  if (w.kind === "query" && !w.fields.active) return "";
  switch (formOf(w)) {
    case "booked":
      return "Booked";
    case "eyeing":
      return "Watching";
    case "roundtrip":
      return "Watching";
    case "flex":
      return "Flexible";
    default:
      return w.fields.booked ? "Booked" : "Hotel";
  }
}

// -- the list -----------------------------------------------------------------

export interface Group {
  trip: Trip;
  watches: Watch[];
  start: string | null;
  end: string | null;
}

const byDate = (a: string | null, b: string | null) =>
  a === b ? 0 : a === null ? 1 : b === null ? -1 : a < b ? -1 : 1;

/**
 * A flight that has already left. The TripIt import archives it the morning
 * after its last day; until then the page hides it itself. A one-day flight
 * goes once its departure time passes, read against this device's clock since
 * `departs` carries no time zone; a round trip, which has no return time, goes
 * once its return day is over.
 */
export function flown(w: Watch, now: Date): boolean {
  if (w.kind !== "flight") return false;
  const end = watchEnd(w);
  if (!end) return false;
  const pad = (n: number) => String(n).padStart(2, "0");
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  if (end !== today) return end < today;
  const departs = w.fields.departs;
  return !w.fields.date_end && typeof departs === "string" && departs !== ""
    && departs <= `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/**
 * A stay from its check-in day on: site-checker has stopped pricing it and a
 * cheaper rate is nothing to act on. The TripIt import archives it after
 * check-out; until then the page hides it itself.
 */
export function checkedIn(w: Watch, now: Date): boolean {
  if (w.kind !== "hotel") return false;
  const checkIn = toIso(w.fields.check_in);
  const pad = (n: number) => String(n).padStart(2, "0");
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return checkIn !== null && checkIn <= today;
}

/** A flight that has left or a stay that has begun: nothing left to watch. */
export function underway(w: Watch, now: Date): boolean {
  return flown(w, now) || checkedIn(w, now);
}

/**
 * Live, unflown watches by trip, in the order they happen. A trip with no live
 * watches still shows unless it is archived, so a trip just created is visible
 * before anything is added to it; a trip whose every live watch has flown
 * does not.
 */
export function tripGroups(dto: FlightsDto, now: Date): Group[] {
  const live = dto.watches.filter((w) => !w.archived);
  const done = new Set(live.filter((w) => underway(w, now)).map((w) => w.trip_id));
  const byTrip = new Map<number, Watch[]>();
  for (const w of live) {
    if (underway(w, now)) continue;
    done.delete(w.trip_id);
    byTrip.set(w.trip_id, [...(byTrip.get(w.trip_id) ?? []), w]);
  }
  const group = (trip: Trip, watches: Watch[]): Group => {
    const sorted = [...watches].sort((a, b) => byDate(watchStart(a), watchStart(b)));
    const starts = sorted.map(watchStart).filter((s): s is string => s !== null);
    const ends = sorted.map(watchEnd).filter((s): s is string => s !== null).sort();
    return { trip, watches: sorted, start: starts[0] ?? null, end: ends[ends.length - 1] ?? null };
  };
  return dto.trips
    .filter((t) => (!t.archived && !done.has(t.id)) || byTrip.has(t.id))
    .map((t) => group(t, byTrip.get(t.id) ?? []))
    .sort((a, b) => byDate(a.start, b.start));
}

export interface Needs {
  matches: { match: Match; watch: Watch | null }[];
  missing: Watch[];
  /** Booked flights with neither a fare nor points: TripIt's feed carries no fares. */
  unpaid: Watch[];
}

export function needsYou(dto: FlightsDto): Needs {
  const live = dto.watches.filter((w) => !w.archived);
  return {
    matches: dto.matches.map((match) => ({
      match,
      watch: dto.watches.find((w) => w.watch_id === match.watch_id) ?? null,
    })),
    missing: live.filter((w) => w.missing_since),
    unpaid: live.filter(
      (w) =>
        formOf(w) === "booked" &&
        !w.missing_since &&
        w.fields.price_tracked == null &&
        w.fields.points_tracked == null,
    ),
  };
}

const flightNo = (v: Value | undefined) =>
  typeof v === "string" ? v.toUpperCase().replace(/\s+/g, "") : "";

/**
 * A live watch this new one would duplicate, or null. The same flight number
 * or route on the same day for a flight; the same name for a query, which
 * site-checker needs unique; the same hotel and check-in for a hotel.
 */
export function findDuplicate(dto: FlightsDto, form: Form, fields: Fields): Watch | null {
  const kind = FORMS[form].kind;
  return (
    dto.watches.find((w) => {
      if (w.archived || w.kind !== kind) return false;
      const f = w.fields;
      if (kind === "query") return Boolean(fields.name) && f.name === fields.name;
      if (kind === "hotel") return f.name === fields.name && f.check_in === fields.check_in;
      if (!fields.date || f.date !== fields.date) return false;
      const same = flightNo(fields.flight);
      return (
        (same !== "" && flightNo(f.flight) === same) ||
        (f.origin === fields.origin && f.destination === fields.destination)
      );
    }) ?? null
  );
}
